import type { CoastalDetailTemplate } from '../../../../shared/scene/coastalDetailTemplates';
import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';
import { drawRopeSpan } from './propDrawingPrimitives';

/** A salt-worn leaning pile carries an exposed rusted tide bell. Rope wraps
 * the timber; the bell has a real open underside and separate clapper. */
function beacon(p: TownPropPalette): string {
  return `<g data-part="tidal-beacon">
    <path d="M8 139L17 131L33 132L44 140V144H8Z" fill="${hex(p.stone)}" />
    <path d="M17 144L30 13L36 5L44 9L32 144Z" fill="${hex(p.wood)}" />
    <path d="M31 144L44 9L39 10L26 144Z" fill="${hex(p.shade)}" />
    <path d="M23 130L28 78L31 49 M31 43L34 18 M35 67L32 95"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.4" />
    <path d="M31 13L36 5L44 9L39 14Z" fill="${hex(p.woodEdge)}" />
    <path d="M29 24L60 20L61 27L29 31Z" fill="${hex(p.wood)}" />
    <path d="M31 25L58 22" stroke="${hex(p.woodEdge)}" stroke-width="1.4" />
    <path d="M30 47L52 29L56 29L31 53Z" fill="${hex(p.wood)}" />
    <path d="M33 46L50 32" stroke="${hex(p.woodEdge)}" stroke-width="1" />
    <path d="M51 26L52 37" stroke="${hex(p.iron)}" stroke-width="2.5" />
    <path d="M49 38V34Q52 30 55 34V38" fill="none" stroke="${hex(p.roof)}" stroke-width="1.8" />
    <path d="M46 38Q52 34 58 38L59 49L63 55L59 58H43L40 55L45 49Z" fill="${hex(p.iron)}" />
    <path d="M46 39Q50 37 52 38L50 48L46 54H42L46 48Z" fill="${hex(p.roof)}" />
    <path d="M54 38L57 40L58 49L60 52" fill="none" stroke="${hex(p.glass)}" stroke-width="1.1" />
    <path d="M41 54Q52 51 62 54L60 58H43Z" fill="${hex(p.roof)}" />
    <ellipse cx="51.5" cy="56" rx="8" ry="2.2" fill="${hex(p.shade)}" />
    <path d="M51 55V62" stroke="${hex(p.iron)}" stroke-width="1.7" />
    <circle cx="51" cy="62" r="2" fill="${hex(p.glass)}" />
    ${drawRopeSpan({ x: 27, y: 74 }, { x: 40, y: 73 }, p, { sag: 2, thickness: 2.6 })}
    ${drawRopeSpan({ x: 26, y: 79 }, { x: 39, y: 78 }, p, { sag: 2, thickness: 2.6 })}
    ${drawRopeSpan({ x: 25, y: 84 }, { x: 39, y: 83 }, p, { sag: 2, thickness: 2.6 })}
    ${drawRopeSpan({ x: 38, y: 83 }, { x: 45, y: 105 }, p, { sag: 3, thickness: 1.8 })}
    <path d="M20 115L23 112L24 118L22 121 M30 128L33 124L34 130 M15 138L19 135L23 138"
      fill="none" stroke="${hex(p.glassCore)}" stroke-width="1.2" opacity=".7" />
    <path d="M8 142H20 M33 142H44" stroke="${hex(p.shade)}" stroke-width="1.5" />
  </g>`;
}

/** Three separate wet stones have uneven tops and real transparent gaps.
 * Short broken reflections sit on upper facets; there is no connecting slab. */
function cobbles(p: TownPropPalette): string {
  return `<g data-part="drowned-cobbles">
    <path d="M3 29Q5 23 12 21L23 22L31 29L29 36H6Z" fill="${hex(p.primary)}" />
    <path d="M3 29L13 31L22 29L31 29L29 36H6Z" fill="${hex(p.shade)}" />
    <path d="M6 27L13 23L22 24L26 27L17 28Z" fill="${hex(p.stone)}" />
    <path d="M40 26L45 15Q50 11 59 12L69 19L72 29L67 36H41L38 32Z" fill="${hex(p.primary)}" />
    <path d="M58 13L69 19L72 29L67 36H58L60 27Z" fill="${hex(p.shade)}" />
    <path d="M44 24L48 17L55 14L63 18L59 23L51 25Z" fill="${hex(p.stone)}" />
    <path d="M78 30L84 24L96 23L107 28L109 36H79Z" fill="${hex(p.primary)}" />
    <path d="M78 31L89 30L100 32L108 30L109 36H79Z" fill="${hex(p.shade)}" />
    <path d="M83 28L88 25L97 25L103 28L95 29Z" fill="${hex(p.stone)}" />
    <path d="M10 25L14 24L19 25 M48 19L52 17L56 17 M88 26H94"
      fill="none" stroke="${hex(p.roofEdge)}" stroke-width="1.1" opacity=".85" />
    <path d="M17 33H20 M48 32H52 M95 34H98" stroke="${hex(p.glassCore)}" stroke-width=".7" opacity=".5" />
  </g>`;
}

export function buildCoastalDetailBody(template: CoastalDetailTemplate, palette: TownPropPalette): string {
  return template === 'tidal_buoy' ? beacon(palette) : cobbles(palette);
}
